// Neu (SPEC 03 §6.4) — Filter- und Rezept-Helfer, Abfrageschlüssel und die
// Adresse der Auswertung. Dieselbe kanonische Form wie SPEC 02 §3.4
// (`rezept.kanonisch`): Listen sortiert und ohne Doppel, Vorgaben weggelassen.
//
// Die Adresse ist die Wahrheit des Dashboards (T15): jede Ansicht ist ein
// Permalink. Filterschlüssel sind die Feldnamen von `Filter`, Werte durch Komma
// getrennt und EINZELN mit encodeURIComponent maskiert — ein Komma IM Wert wird
// so zu %2C und bleibt vom Trenner unterscheidbar (darum hier von Hand gebaut
// und gelesen statt über URLSearchParams, das beides gleich behandeln würde).
import type { Dim, Filter, KachelDef, Rezept } from "../api/typen";

export const LISTEN = [
  "standort",
  "altersgruppe",
  "schule",
  "klasse",
  "charge",
  "thema",
  "oberthema",
  "frage",
  "feld_option",
  "skala_wert",
  "ding",
  "stadtteil",
  "ort",
] as const; // = 02 §3.4 LISTEN (+ stadtteil, ort: E21, 05 §15.6)
export type ListenSchluessel = (typeof LISTEN)[number];

export const MERKMAL = new Set<string>(["standort", "altersgruppe", "schule", "klasse", "charge"]);
export const INHALT = new Set<string>(["thema", "oberthema", "frage", "feld_option", "skala_wert", "ding", "stadtteil", "ort"]);

/** E21: worauf sich ein Stadtteil-Filter bezieht — „ort“ = alles außer Wohnort. Vorgabe "alle". */
export const ORTSBEZUEGE = ["alle", "ort", "wohnort"] as const;
export type Ortsbezug = (typeof ORTSBEZUEGE)[number];

/** Die Filterschlüssel in der Reihenfolge der Filterleiste (SPEC 03 §9.3.5). */
export const FILTER_REIHENFOLGE = [
  "standort",
  "altersgruppe",
  "schule",
  "klasse",
  "charge",
  "thema",
  "oberthema",
  "frage",
  "feld_option",
  "skala_wert",
  "ding",
  "stadtteil",
  "ort",
  "ortsbezug",
  "pruefstatus",
  "aus_zeichnung",
] as const;

function istListe(k: string): k is ListenSchluessel {
  return (LISTEN as readonly string[]).includes(k);
}

/** Listenwerte sortiert und ohne Doppel — Zahlen numerisch, Texte nach Codepunkt (wie Pythons sorted). */
function ordne(k: ListenSchluessel, werte: readonly (string | number)[]): (string | number)[] {
  if (k === "skala_wert") {
    const zahlen = [...new Set(werte.map((w) => Number(w)).filter((w) => Number.isFinite(w)))];
    return zahlen.sort((a, b) => a - b);
  }
  const texte = [...new Set(werte.map((w) => String(w)))];
  return texte.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** Ohne Vorgaben, Listen sortiert und ohne Doppel — Grundlage für Schlüssel, URL und filterB64. */
export function kompakt(f: Filter): Filter {
  const aus: Record<string, unknown> = {};
  // Schlüssel alphabetisch einsetzen: JSON.stringify folgt der Einfügereihenfolge.
  const schluessel = Object.keys(f).sort();
  for (const k of schluessel) {
    const v = (f as Record<string, unknown>)[k];
    if (v === undefined || v === null) continue;
    if (istListe(k)) {
      const liste = Array.isArray(v) ? ordne(k, v as (string | number)[]) : [];
      if (liste.length) aus[k] = liste;
    } else if (k === "pruefstatus") {
      // E36: Vorgabe ist „angenommen“ (jede angenommene Antwort zählt); „geprueft“ steht ausdrücklich da.
      if (v !== "angenommen") aus[k] = v;
    } else if (k === "aus_zeichnung") {
      if (v !== "alle") aus[k] = v;
    } else if (k === "bogenart") {
      if (v !== "einzel") aus[k] = v;
    } else if (k === "ortsbezug") {
      // Nur gesetzt im JSON: so bleiben alte Rezept-Hashes und Belegmengen gleich (05 §15.6).
      if (v === "ort" || v === "wohnort") aus[k] = v;
    }
  }
  return aus as Filter;
}

/** Stabiler Schlüssel eines Filters (Abfrageschlüssel, Vergleich). */
export function filterSchluessel(f: Filter): string {
  return JSON.stringify(kompakt(f));
}

/** Wie viele Filterschlüssel gesetzt sind — eine Bedienzahl („Filter (2)"), keine Datenzahl. */
export function anzahlFilter(f: Filter): number {
  return Object.keys(kompakt(f)).length; // T14: keine Anzeige
}

/** JSON mit rekursiv sortierten Schlüsseln. */
function stabil(v: unknown): string {
  if (Array.isArray(v)) return "[" + v.map(stabil).join(",") + "]";
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return (
      "{" +
      Object.keys(o)
        .filter((k) => o[k] !== undefined)
        .sort()
        .map((k) => JSON.stringify(k) + ":" + stabil(o[k]))
        .join(",") +
      "}"
    );
  }
  return JSON.stringify(v ?? null);
}

/** Rezept mit ausgeschriebenen Vorgaben (wie 02 §3.4: exclude_defaults=False). */
export function rezeptVoll(r: Rezept): Required<Omit<Rezept, "filter">> & { filter: Filter } {
  return {
    version: 1,
    metrik: r.metrik,
    gruppiere_nach: [...(r.gruppiere_nach ?? [])],
    filter: kompakt(r.filter ?? {}),
    anteil: r.anteil ?? "basis",
    mit_keine_angabe: r.mit_keine_angabe ?? true,
    ordnung: r.ordnung ?? "wert_ab",
    grenze: r.grenze ?? null,
    katalog_version: r.katalog_version ?? null,
  };
}

/** Kanonischer Schlüssel eines Rezepts: sortierte Schlüssel, Filter kompakt, Vorgaben geschrieben (R03-5). */
export function rezeptSchluessel(r: Rezept): string {
  return stabil(rezeptVoll(r));
}

/** webgis basisFilterFuer: der globale Filter OHNE die eigene Dimension der Kachel. */
export function basisFilterFuer(f: Filter, eigene: Dim | null): Filter {
  if (!eigene) return { ...f };
  const aus: Record<string, unknown> = { ...f };
  delete aus[eigene];
  return aus as Filter;
}

/**
 * Kachel-Rezept + Dashboard-Filter (ohne eigene Dim) = das Rezept, das gezählt wird.
 * Der eigene Filter der Kachel gewinnt für die Schlüssel, die er setzt
 * (`bewertung_{F}`: frage; `kpi_geprueft`/`offen`: pruefstatus). Setzt die
 * Kachel eine ANDERE Frage als das Dashboard, fallen dessen feld_option und
 * skala_wert weg — sie gehören zu genau einer Frage (02 §3.3).
 * „Setzt“ heißt: weicht von der Vorgabe ab — der Server schickt den ganzen Filter (leere Listen,
 * `pruefstatus: "angenommen"`); kompakt nimmt die Vorgaben heraus, sonst überschrieben sie den
 * Dashboard-Filter (E36: der Prüfmodus der Ansicht gilt auch in den Kacheln).
 */
export function kachelRezept(k: KachelDef, f: Filter): Rezept {
  const eigen = kompakt(k.rezept.filter ?? {});
  const basis: Record<string, unknown> = { ...basisFilterFuer(f, k.eigene_dim) };
  if (eigen.frage && eigen.frage.length) {
    const dashFrage = [...(f.frage ?? [])].sort().join(",");
    const kachelFrage = [...eigen.frage].sort().join(",");
    if (dashFrage !== kachelFrage) {
      delete basis.feld_option;
      delete basis.skala_wert;
    }
  }
  return { ...k.rezept, filter: kompakt({ ...(basis as Filter), ...eigen }) };
}

/** Eine Liste in einem Filter setzen (leer = entfernen) — gibt einen NEUEN Filter zurück. */
export function mitListe(f: Filter, k: ListenSchluessel, werte: readonly (string | number)[]): Filter {
  const aus: Record<string, unknown> = { ...f };
  if (werte.length) aus[k] = k === "skala_wert" ? werte.map((w) => Number(w)) : werte.map((w) => String(w));
  else delete aus[k];
  // Ohne genau eine Frage gibt es keine Antwortoption und keinen Skalenwert (02 §3.3).
  if (k === "frage" && (werte.length !== 1 || (f.frage ?? []).join(",") !== werte.join(","))) {
    delete aus.feld_option;
    delete aus.skala_wert;
  }
  return aus as Filter;
}

/** Einen Schlüssel ganz entfernen; `frage` nimmt feld_option und skala_wert mit. */
export function ohneSchluessel(f: Filter, k: string): Filter {
  const aus: Record<string, unknown> = { ...f };
  delete aus[k];
  if (k === "frage") {
    delete aus.feld_option;
    delete aus.skala_wert;
  }
  return aus as Filter;
}

/** Wert in einer Liste umschalten. */
export function umschalten(f: Filter, k: ListenSchluessel, wert: string): Filter {
  const alt = ((f[k] ?? []) as (string | number)[]).map(String);
  const neu = alt.includes(wert) ? alt.filter((w) => w !== wert) : [...alt, wert];
  return mitListe(f, k, neu);
}

/** Die gesetzten Werte einer Liste als Texte (für Chips und Hervorhebung). */
export function werteVon(f: Filter, k: string): string[] {
  const v = (f as Record<string, unknown>)[k];
  return Array.isArray(v) ? v.map(String) : [];
}

// ============================================================ Adresse (URL)

export type AuswertungTab = "ueberblick" | "matrix" | "antworten" | "zitate";
export type MatrixSpalte = "altersgruppe" | "standort" | "schule" | "klasse" | "skala_wert";
export type MatrixAnteil = "spalte" | "zeile" | "basis";
export type AntwortSort = "code" | "bogen" | "frage";
export type DockStufe = "halb" | "voll" | "ganz";

export interface AuswertungUi {
  tab: AuswertungTab;
  spalten: MatrixSpalte;
  anteil: MatrixAnteil;
  /** Kachel-IDs in Tabellenansicht; "matrix" für den Matrix-Reiter */
  tabelle: string[];
  sort: AntwortSort;
  /** Blätterzeiger der Antwortliste, wird nur durchgereicht */
  nach: string | null;
}

export interface DockZustand {
  menge: string | null;
  stufe: DockStufe;
  sel: number;
}

export interface AuswertungZustand {
  filter: Filter;
  ui: AuswertungUi;
  dock: DockZustand;
}

export const UI_VORGABE: AuswertungUi = { tab: "ueberblick", spalten: "altersgruppe", anteil: "spalte", tabelle: [], sort: "code", nach: null };
export const DOCK_VORGABE: DockZustand = { menge: null, stufe: "halb", sel: 1 };

const TABS: readonly AuswertungTab[] = ["ueberblick", "matrix", "antworten", "zitate"];
const SPALTEN: readonly MatrixSpalte[] = ["altersgruppe", "standort", "schule", "klasse", "skala_wert"];
const ANTEILE: readonly MatrixAnteil[] = ["spalte", "zeile", "basis"];
const SORTE: readonly AntwortSort[] = ["code", "bogen", "frage"];
const STUFEN: readonly DockStufe[] = ["halb", "voll", "ganz"];

function dekodiere(s: string): string {
  try {
    return decodeURIComponent(s.replace(/\+/g, " "));
  } catch {
    return s;
  }
}

/** Rohe Suchzeile in Schlüssel → Werteliste zerlegen (Komma trennt, %2C nicht). */
export function zerlegeSuche(suche: string): Map<string, string[]> {
  const aus = new Map<string, string[]>();
  const roh = suche.startsWith("?") ? suche.slice(1) : suche;
  if (!roh) return aus;
  for (const teil of roh.split("&")) {
    if (!teil) continue;
    const i = teil.indexOf("=");
    const k = dekodiere(i < 0 ? teil : teil.slice(0, i));
    const v = i < 0 ? "" : teil.slice(i + 1);
    const werte = v === "" ? [] : v.split(",").map(dekodiere).filter((w) => w !== "");
    aus.set(k, werte);
  }
  return aus;
}

function eins<T extends string>(m: Map<string, string[]>, k: string, erlaubt: readonly T[], vorgabe: T): T {
  const v = m.get(k)?.[0];
  return v && (erlaubt as readonly string[]).includes(v) ? (v as T) : vorgabe;
}

/** Filter aus der Suchzeile lesen. Ungültige Werte gehen unverändert an den Server (400 räumt sie weg). */
export function filterAusSuche(suche: string): Filter {
  const m = zerlegeSuche(suche);
  const f: Record<string, unknown> = {};
  for (const k of LISTEN) {
    const w = m.get(k) ?? (URL_KURZ[k] ? m.get(URL_KURZ[k]) : undefined);
    if (!w || !w.length) continue;
    if (k === "skala_wert") {
      const zahlen = w.map((x) => Number(x)).filter((x) => Number.isInteger(x));
      if (zahlen.length) f[k] = zahlen;
    } else f[k] = w;
  }
  const ps = m.get("pruefstatus")?.[0];
  // E36: ohne Angabe „angenommen“ (auch Adressen von vor E36, die „geprueft“ wegließen — DECISIONS E36);
  // der Assistent schreibt den Modus immer ausdrücklich, auch „angenommen“
  if (ps === "alle" || ps === "geprueft" || ps === "angenommen") f.pruefstatus = ps;
  const az = m.get("aus_zeichnung")?.[0];
  if (az === "nur" || az === "ohne") f.aus_zeichnung = az;
  const ob = (m.get("ortsbezug") ?? m.get("ob"))?.[0];
  if (ob === "ort" || ob === "wohnort") f.ortsbezug = ob;
  return kompakt(f as Filter);
}

/** Kurznamen aus 05 §10 (`st`, `o`, `ob`), die beim LESEN auch gelten. Geschrieben werden die
 *  Feldnamen selbst (03 §6.4: „Filterschlüssel wörtlich“) — so baut sie auch der Assistent
 *  (`werkzeuge.ansicht_url`). */
const URL_KURZ: Partial<Record<ListenSchluessel, string>> = { stadtteil: "st", ort: "o" };

/** Den ganzen Zustand der Auswertung aus der Suchzeile lesen. */
export function leseAuswertung(suche: string): AuswertungZustand {
  const m = zerlegeSuche(suche);
  const sel = Number(m.get("sel")?.[0]);
  return {
    filter: filterAusSuche(suche),
    ui: {
      tab: eins(m, "tab", TABS, UI_VORGABE.tab),
      spalten: eins(m, "spalten", SPALTEN, UI_VORGABE.spalten),
      anteil: eins(m, "anteil", ANTEILE, UI_VORGABE.anteil),
      tabelle: m.get("tabelle") ?? [],
      sort: eins(m, "sort", SORTE, UI_VORGABE.sort),
      nach: m.get("nach")?.[0] ?? null,
    },
    dock: {
      menge: m.get("menge")?.[0] ?? null,
      stufe: eins(m, "stufe", STUFEN, DOCK_VORGABE.stufe),
      sel: Number.isInteger(sel) && sel >= 1 ? sel : DOCK_VORGABE.sel,
    },
  };
}

function paar(k: string, werte: readonly (string | number)[]): string {
  return `${encodeURIComponent(k)}=${werte.map((w) => encodeURIComponent(String(w))).join(",")}`;
}

/** Nur der Filterteil der Suchzeile (ohne "?"), in fester Schlüsselreihenfolge. */
export function filterSuche(f: Filter): string {
  const k = kompakt(f);
  const teile: string[] = [];
  for (const s of LISTEN) {
    const v = k[s] as (string | number)[] | undefined;
    if (v && v.length) teile.push(paar(s, v));
  }
  if (k.pruefstatus === "alle" || k.pruefstatus === "geprueft") teile.push(`pruefstatus=${k.pruefstatus}`);
  if (k.aus_zeichnung && k.aus_zeichnung !== "alle") teile.push(`aus_zeichnung=${k.aus_zeichnung}`);
  if (k.ortsbezug && k.ortsbezug !== "alle") teile.push(`ortsbezug=${k.ortsbezug}`);
  return teile.join("&");
}

/** Suchzeile (ohne "?") aus dem ganzen Zustand bauen; Vorgaben fallen weg. */
export function baueAuswertung(z: { filter: Filter; ui?: Partial<AuswertungUi>; dock?: Partial<DockZustand> }): string {
  const teile: string[] = [];
  const fs = filterSuche(z.filter);
  if (fs) teile.push(fs);
  const ui = { ...UI_VORGABE, ...(z.ui ?? {}) };
  if (ui.tab !== UI_VORGABE.tab) teile.push(`tab=${ui.tab}`);
  if (ui.spalten !== UI_VORGABE.spalten) teile.push(`spalten=${ui.spalten}`);
  if (ui.anteil !== UI_VORGABE.anteil) teile.push(`anteil=${ui.anteil}`);
  if (ui.tabelle.length) teile.push(paar("tabelle", [...new Set(ui.tabelle)]));
  if (ui.sort !== UI_VORGABE.sort) teile.push(`sort=${ui.sort}`);
  if (ui.nach) teile.push(paar("nach", [ui.nach]));
  const dock = { ...DOCK_VORGABE, ...(z.dock ?? {}) };
  if (dock.menge) {
    teile.push(paar("menge", [dock.menge]));
    if (dock.stufe !== DOCK_VORGABE.stufe) teile.push(`stufe=${dock.stufe}`);
    if (dock.sel !== DOCK_VORGABE.sel) teile.push(`sel=${dock.sel}`);
  }
  return teile.join("&");
}

/** Vollständiger Pfad der Auswertung eines Verfahrens mit Filter (und optional offener Menge). */
export function auswertungsPfad(V: string, f: Filter, extra?: { ui?: Partial<AuswertungUi>; dock?: Partial<DockZustand> }): string {
  const s = baueAuswertung({ filter: f, ui: extra?.ui, dock: extra?.dock });
  return `/v/${encodeURIComponent(V)}/auswerten${s ? "?" + s : ""}`;
}

/**
 * Pfad der Karte eines Verfahrens (E32, 05 §19) mit dem Filter der Auswertung — ohne Stadtteil und
 * Ort (die Karte zeigt sie alle und hebt nichts weg). `beitraege` schaltet dort die Ebene
 * „Beiträge“ an (`?zeige=beitraege`, die Karte merkt es sich und nimmt den Schalter aus der Adresse).
 */
export function kartenPfad(V: string, f: Filter = {}, beitraege = false): string {
  const ohne: Record<string, unknown> = { ...f };
  delete ohne.stadtteil;
  delete ohne.ort;
  const teile = [filterSuche(ohne as Filter), beitraege ? "zeige=beitraege" : ""].filter((x) => x !== "");
  return `/v/${encodeURIComponent(V)}/karte${teile.length ? "?" + teile.join("&") : ""}`;
}

/** Filterwert gegen die Feldmuster aus 02 §3.3 — nur zur Vorprüfung vor dem Senden. */
export function wertPasst(k: ListenSchluessel, w: string): boolean {
  switch (k) {
    case "standort":
      return /^[a-z0-9-]{1,32}$/.test(w);
    case "altersgruppe":
      return /^(\d{1,2}[–-]\d{1,2}|\d{1,2}\+|__ka)$/.test(w);
    case "thema":
    case "oberthema":
      return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(w);
    case "frage":
      return /^[A-Za-z][A-Za-z0-9_.-]{0,15}$/.test(w);
    case "feld_option":
      return /^[a-z0-9_]{1,32}$/.test(w);
    case "skala_wert":
      return /^-?\d{1,2}$/.test(w);
    case "stadtteil":
      // E21h: dazu „außerhalb der Stadt“ (__aus) und „unklar“ (__unklar) — wie rezept._MUSTER im Backend.
      return w.length <= 60 && /^([a-z0-9]+(-[a-z0-9]+)*|__ka|__aus|__unklar)$/.test(w);
    case "ort":
      return w.length <= 80 && /^[a-z0-9]+(-[a-z0-9]+)*$/.test(w);
    default:
      return w.length > 0 && w.length <= 80;
  }
}
